import axiosInstance from "../../../common/infrastructure/helpers/axiosHelper";
import { buildRequestHeaders } from "../../../common/infrastructure/helpers/axiosHeaders";
import { getSelectedStudioId } from "../../../common/infrastructure/hooks/useAuthentication";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { AiSendOutcome, AiStreamEvent, IAiSendRequest } from "../models/aiAssistantModels";
import { parseStreamEvent, readErrorBody } from "../utilities/aiAssistantDataUtility";

/**
 * The streaming half of the assistant: POST AiAssistant/Send with fetch (axios 0.21 cannot read a
 * response while it arrives) and read its text/event-stream. EventSource is no option: it cannot
 * send the Bearer token or X-Studio-Id.
 */

export interface ISseParser {
  /** Feeds decoded text; the chunk may end anywhere, even inside a line ending. */
  push: (chunk: string) => void;
  /** Delivers a last event that had no blank line after it (the stream just closed). */
  flush: () => void;
}

/**
 * Splits a text/event-stream into the data of its events. Lines starting with ":" are comments
 * (keep-alives) and are skipped; "event:", "id:" and "retry:" fields are not used. Several
 * "data:" lines of one event are joined with "\n", as the spec says.
 */
export const createSseParser = (onData: (data: string) => void): ISseParser => {
  let buffer = "";
  // A chunk that ends with "\r" may be the first half of a "\r\n"; decide with the next chunk.
  let pendingCarriageReturn = false;

  const dispatch = (block: string) => {
    const dataLines: string[] = [];
    block.split("\n").forEach((line) => {
      if (line === "" || line.startsWith(":")) return;
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "data") dataLines.push(value);
    });
    if (dataLines.length > 0) {
      onData(dataLines.join("\n"));
    }
  };

  const drain = () => {
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      dispatch(block);
      boundary = buffer.indexOf("\n\n");
    }
  };

  return {
    push: (chunk: string) => {
      let text = pendingCarriageReturn ? `\r${chunk}` : chunk;
      pendingCarriageReturn = text.endsWith("\r");
      if (pendingCarriageReturn) text = text.slice(0, -1);
      buffer += text.replace(/\r\n?/g, "\n");
      drain();
    },
    flush: () => {
      if (pendingCarriageReturn) {
        buffer += "\n";
        pendingCarriageReturn = false;
      }
      drain();
      if (buffer.trim() !== "") dispatch(buffer);
      buffer = "";
    },
  };
};

const isAbortError = (error: unknown): boolean =>
  (error as { name?: string } | null)?.name === "AbortError";

const toHeaderRecord = (headers: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)]),
  );

const readBodyText = async (response: Response): Promise<string> => {
  try {
    return await response.text();
  } catch {
    return "";
  }
};

export interface ISendAiAssistantMessageArgs {
  request: IAiSendRequest;
  authToken?: string;
  signal?: AbortSignal;
  onEvent: (event: AiStreamEvent) => void;
  /** For tests; the browser's fetch otherwise. */
  fetchImpl?: typeof fetch;
}

/**
 * Sends one message and reports every stream event through `onEvent`. Never throws: how the
 * call ended comes back as an {@link AiSendOutcome}, so the caller decides what the user sees.
 */
export const sendAiAssistantMessage = async ({
  request,
  authToken,
  signal,
  onEvent,
  fetchImpl,
}: ISendAiAssistantMessageArgs): Promise<AiSendOutcome> => {
  // Called through a wrapper: some browsers throw "Illegal invocation" for a detached fetch.
  const doFetch: typeof fetch = fetchImpl ?? ((input, init) => window.fetch(input, init));
  const headers = buildRequestHeaders(
    {
      Accept: "text/event-stream",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    getSelectedStudioId(),
  );

  let response: Response;
  try {
    response = await doFetch(`${axiosInstance.defaults.baseURL}${endpointsMetadata.aiAssistant.send}`, {
      method: "POST",
      headers: toHeaderRecord(headers),
      body: JSON.stringify(request),
      signal,
    });
  } catch (error) {
    return isAbortError(error) ? { kind: "aborted" } : { kind: "network" };
  }

  if (!response.ok) {
    const { message, code } = readErrorBody(await readBodyText(response));
    return { kind: "rejected", status: response.status, message, code };
  }

  let finished = false;
  const parser = createSseParser((data) => {
    const event = parseStreamEvent(data);
    if (!event) return;
    if (event.type === "done" || event.type === "error") finished = true;
    onEvent(event);
  });

  const reader = response.body?.getReader();
  if (!reader) {
    // No streaming body (very old browser): the whole answer arrives at once.
    parser.push(await readBodyText(response));
    parser.flush();
    return finished ? { kind: "completed" } : { kind: "interrupted" };
  }

  const decoder = new TextDecoder("utf-8");
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // stream: true keeps a Cyrillic character that a chunk boundary split in two.
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.flush();
  } catch (error) {
    if (finished) return { kind: "completed" };
    return isAbortError(error) || signal?.aborted ? { kind: "aborted" } : { kind: "interrupted" };
  }

  return finished ? { kind: "completed" } : { kind: "interrupted" };
};
